/**
 * Our Brands Section
 * Handles interactive brand selection with logo buttons and dot indexer navigation.
 * Implements accessible tab panel pattern with keyboard navigation.
 */

class OurBrands extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.brandCount = 0;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchEndX = 0;
    this.touchEndY = 0;
    this.minSwipeDistance = 50;
    this.resizeObserver = null;
    this.resizeTimeout = null;
  }

  connectedCallback() {
    this.logoButtons = this.querySelectorAll('.our-brands__logo-button');
    this.indexerDots = this.querySelectorAll('.our-brands__indexer-dot');
    this.contentPanels = this.querySelectorAll('.our-brands__content-panel');
    this.contentWrapper = this.querySelector('.our-brands__content-wrapper');

    this.brandCount = this.logoButtons.length;

    if (this.brandCount === 0) return;

    this.setFixedHeight();
    this.setupResizeObserver();
    this.bindEvents();
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  /**
   * Check if device is mobile based on viewport width
   * @returns {boolean} True if mobile device
   */
  isMobile() {
    return window.innerWidth <= 749;
  }

  /**
   * Calculate the tallest panel and set fixed height on wrapper
   * Uses CSS Grid so all panels stack naturally and wrapper height matches tallest
   * On mobile, uses auto height to allow proper resizing
   */
  setFixedHeight() {
    if (!this.contentWrapper || this.contentPanels.length === 0) return;

    // On mobile, use auto height to allow proper resizing
    if (this.isMobile()) {
      this.contentWrapper.style.height = 'auto';
      return;
    }

    let maxHeight = 0;

    // Temporarily show all panels to measure their heights
    this.contentPanels.forEach((panel) => {
      const originalHidden = panel.hidden;
      const originalClasses = panel.className;
      
      // Temporarily show panel to measure (still in grid, just visible)
      panel.hidden = false;
      panel.style.visibility = 'hidden';
      panel.style.opacity = '1';
      panel.style.pointerEvents = 'none';
      panel.classList.remove('our-brands__content-panel--active');
      
      // Force reflow to get accurate measurement
      void panel.offsetHeight;
      
      // Measure height
      const height = panel.offsetHeight;
      if (height > maxHeight) {
        maxHeight = height;
      }
      
      // Restore original state
      panel.hidden = originalHidden;
      panel.className = originalClasses;
      panel.style.visibility = '';
      panel.style.opacity = '';
      panel.style.pointerEvents = '';
    });

    // Set fixed height on wrapper to prevent layout shift (desktop only)
    if (maxHeight > 0) {
      this.contentWrapper.style.height = `${maxHeight}px`;
    }
  }

  /**
   * Setup ResizeObserver to recalculate heights on viewport changes
   * Fixes mobile layout issues when orientation changes or browser resizes
   */
  setupResizeObserver() {
    if (!this.contentWrapper || !window.ResizeObserver) {
      // Fallback to window resize if ResizeObserver not available
      window.addEventListener('resize', () => {
        if (this.resizeTimeout) {
          clearTimeout(this.resizeTimeout);
        }
        this.resizeTimeout = setTimeout(() => {
          this.setFixedHeight();
        }, 150);
      });
      return;
    }

    this.resizeObserver = new ResizeObserver((entries) => {
      // Debounce resize events
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = setTimeout(() => {
        this.setFixedHeight();
      }, 150);
    });

    this.resizeObserver.observe(this.contentWrapper);
    
    // Also observe the document body for orientation changes
    if (document.body) {
      this.resizeObserver.observe(document.body);
    }
  }

  /**
   * Bind event listeners for logo buttons, indexer dots, keyboard navigation, and touch/swipe
   */
  bindEvents() {
    // Logo button click events
    this.logoButtons.forEach((button, index) => {
      button.addEventListener('click', () => this.selectBrand(index));
      button.addEventListener('keydown', (e) => this.handleKeydown(e, index, this.logoButtons));
    });

    // Indexer dot click events - use data-brand-index for reliability
    this.indexerDots.forEach((dot) => {
      const brandIndex = parseInt(dot.getAttribute('data-brand-index'), 10);
      if (!isNaN(brandIndex)) {
        dot.addEventListener('click', () => this.selectBrand(brandIndex));
        dot.addEventListener('keydown', (e) => this.handleKeydown(e, brandIndex, this.indexerDots));
      }
    });

    // Touch/swipe events for mobile
    if (this.contentWrapper) {
      this.contentWrapper.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
      this.contentWrapper.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: true });
      this.contentWrapper.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });
    }
  }

  /**
   * Handle touch start for swipe detection
   * @param {TouchEvent} event - The touch event
   */
  handleTouchStart(event) {
    const firstTouch = event.touches[0];
    this.touchStartX = firstTouch.clientX;
    this.touchStartY = firstTouch.clientY;
  }

  /**
   * Handle touch move (track current position)
   * @param {TouchEvent} event - The touch event
   */
  handleTouchMove(event) {
    // Just track the position, don't prevent default to allow scrolling
    const firstTouch = event.touches[0];
    this.touchEndX = firstTouch.clientX;
    this.touchEndY = firstTouch.clientY;
  }

  /**
   * Handle touch end and detect swipe gesture
   * @param {TouchEvent} event - The touch event
   */
  handleTouchEnd(event) {
    if (!this.touchStartX || !this.touchEndX) return;

    const deltaX = this.touchEndX - this.touchStartX;
    const deltaY = this.touchEndY - this.touchStartY;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    // Only trigger swipe if horizontal movement is greater than vertical (horizontal swipe)
    // and exceeds minimum distance
    if (absDeltaX > absDeltaY && absDeltaX > this.minSwipeDistance) {
      if (deltaX > 0) {
        // Swipe right - go to previous brand
        const prevIndex = this.currentIndex === 0 ? this.brandCount - 1 : this.currentIndex - 1;
        this.selectBrand(prevIndex);
      } else {
        // Swipe left - go to next brand
        const nextIndex = this.currentIndex === this.brandCount - 1 ? 0 : this.currentIndex + 1;
        this.selectBrand(nextIndex);
      }
    }

    // Reset touch coordinates
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchEndX = 0;
    this.touchEndY = 0;
  }

  /**
   * Handle keyboard navigation for tab pattern
   * @param {KeyboardEvent} event - The keyboard event
   * @param {number} currentIndex - Current focused element index
   * @param {NodeList} elements - Collection of navigable elements
   */
  handleKeydown(event, currentIndex, elements) {
    let newIndex = currentIndex;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        newIndex = currentIndex === 0 ? this.brandCount - 1 : currentIndex - 1;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        newIndex = currentIndex === this.brandCount - 1 ? 0 : currentIndex + 1;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = this.brandCount - 1;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.selectBrand(currentIndex);
        return;
      default:
        return;
    }

    // Focus the new element and select the brand
    elements[newIndex].focus();
    this.selectBrand(newIndex);
  }

  /**
   * Select a brand by index, updating UI state for all interactive elements
   * @param {number} index - The brand index to select
   */
  selectBrand(index) {
    if (index === this.currentIndex || index < 0 || index >= this.brandCount) {
      return;
    }

    // Update logo buttons
    this.logoButtons.forEach((button, i) => {
      const isActive = i === index;
      button.classList.toggle('our-brands__logo-button--active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    // Update indexer dots - use data-brand-index to match correctly
    this.indexerDots.forEach((dot) => {
      const dotIndex = parseInt(dot.getAttribute('data-brand-index'), 10);
      if (!isNaN(dotIndex)) {
        const isActive = dotIndex === index;
        dot.classList.toggle('our-brands__indexer-dot--active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      }
    });

    // Update content panels
    this.contentPanels.forEach((panel, i) => {
      const isActive = i === index;
      panel.classList.toggle('our-brands__content-panel--active', isActive);
      panel.hidden = !isActive;
    });

    this.currentIndex = index;
  }
}

// Define the custom element
if (!customElements.get('our-brands')) {
  customElements.define('our-brands', OurBrands);
}




